/**
 * The things standing about the level that are not people and are not a
 * station the run has to use: doors and their handles, the sauna, cylinders,
 * chimney smoke, the projector's throw, the tent's lighting rig, the signs,
 * the screens, the hoses and the camp's tents.
 *
 * Mostly presentation, with two exceptions the fingerprint does hash: a door's
 * swinging leaf and a camp tent's footprint.
 */

import type { Vec3 } from '../utils/math';
import { boxCenter, boxSize, type Box, type MaterialKey } from './levelBox';
import { ext } from './levelAuthoring';

export interface DoorSpec {
  id: string;
  /**
   * The two halves of the prompt, shown after `[F]`: what a shut leaf offers
   * and what an open one does. Both are authored rather than derived, because
   * a prompt built by swapping the first word of a Finnish one is a prompt
   * that reads wrong the first time a door is called anything else.
   *
   * Dressing, like `SaunaSpec.label`: neither string is in
   * `simulationFingerprint()`, so a door can be relabelled without retiring a
   * single leaderboard time.
   */
  label: string;
  closeLabel: string;
  /**
   * The leaf, shut. It is the collider at angle zero and it is also what F
   * aims at, so it has to be the slab itself rather than the opening it fills.
   *
   * Two things about it are load-bearing for `doorLeafBoxes`, and
   * `Level.test.ts` holds both: the hinge sits on the leaf's centre line
   * across its thickness, and it sits at one *end* of its length. A hinge
   * anywhere else describes a door nobody has ever hung.
   */
  box: Box;
  /** Vertical hinge axis, in world XZ. */
  hinge: [number, number];
  /** +1 / -1 - which way the leaf swings. */
  swing: number;
}

/**
 * Where a door's leaf actually is, as boxes, at a given swing angle.
 *
 * The leaf is the one solid in this level that moves through ground a runner
 * is standing on, and collision is axis-aligned boxes and nothing else - so a
 * leaf at forty degrees has to be approximated rather than rotated. One box
 * round the whole thing would be a metre square at 45 deg and would shove
 * people who were nowhere near it, so it is diced along its length into
 * `DOOR.segments` pieces and each piece gets its own bounding box. Same trick
 * and same reason as the staves of the palju's rim - see `tubRim`.
 *
 * The rotation is exactly the one three.js gives the leaf's group, because
 * `World.renderDoors` and this function read the same angle off the same
 * `Door`: what you can see swinging is what you run into.
 *
 * Pure arithmetic on the spec and no clock of its own, so the collision build,
 * the door itself and the tests all measure one leaf.
 */
export function doorLeafBoxes(spec: DoorSpec, angle: number, segments: number): Box[] {
  const size = boxSize(spec.box);
  const center = boxCenter(spec.box);
  // Which way the leaf is long, and therefore which axis the hinge is at one
  // end of. Every door in the level is a thin slab, so this is never a guess.
  const alongZ = size.x < size.z;
  const half = (alongZ ? size.x : size.z) / 2;
  const length = alongZ
    ? Math.max(spec.box.maxZ - spec.hinge[1], spec.hinge[1] - spec.box.minZ)
    : Math.max(spec.box.maxX - spec.hinge[0], spec.hinge[0] - spec.box.minX);
  // Which end the free edge is at, as a sign: the hinge is at one end of the
  // leaf and the rest of it reaches away towards the middle of the box.
  const reach = alongZ ? Math.sign(center.z - spec.hinge[1]) : Math.sign(center.x - spec.hinge[0]);

  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const boxes: Box[] = [];
  for (let i = 0; i < segments; i++) {
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    // The four corners of this slice, taken round the leaf's own frame - the
    // hinge at the origin, `along` out towards the free edge and `across` the
    // thickness - and then turned into the room.
    for (const along of [(length * i) / segments, (length * (i + 1)) / segments]) {
      for (const across of [-half, half]) {
        const localX = alongZ ? across : reach * along;
        const localZ = alongZ ? reach * along : across;
        const x = spec.hinge[0] + localX * cos + localZ * sin;
        const z = spec.hinge[1] - localX * sin + localZ * cos;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minZ = Math.min(minZ, z);
        maxZ = Math.max(maxZ, z);
      }
    }
    boxes.push({ minX, minY: spec.box.minY, minZ, maxX, maxY: spec.box.maxY, maxZ });
  }
  return boxes;
}

/**
 * ==========================================================================
 *  THE ONE DOOR IN THE VENUE THAT DOES NOT OPEN
 * ==========================================================================
 *  The saunatilat are on the floor plan and they are behind the juhlasali's
 *  east wall, so the hall has a door into them. It is locked, and it stays
 *  locked: somebody is in there, and there is a man standing beside it whose
 *  whole job this morning is to say so.
 *
 *  It is deliberately not a `DoorSpec`, and that is the whole design of it. A
 *  `Door` owns a collider it takes away on a clock; this one has nothing to
 *  take away, because the wall it is hung on stays solid whatever anybody
 *  does to the handle. So the leaf, its karmi and its painike are `decor`
 *  exactly like the keittiö's and the tornimajoitus's, and this struct is
 *  only the half F needs: something to reach for, and something to print
 *  after `[F]`.
 *
 *  Which is also why it is outside `simulationFingerprint()`. Nothing here
 *  can change where a player ends up - the wall refuses him whether he tries
 *  the handle or not - and scenery must not retire the board. See
 *  src/config/CLAUDE.md, and src/world/Sauna.ts for the thing that reads it.
 * ==========================================================================
 */
export interface SaunaSpec {
  /** Reported as `StepEvents.interacted`, which is how the man beside it hears the handle. */
  id: string;
  /** Shown in the HUD after `[F]`, exactly as a door's label is. */
  label: string;
  /** The leaf. Drawn out of `decor`; this box is only what F aims at. */
  box: Box;
  /**
   * The man standing at it, by the name he has in `standers`.
   *
   * Here rather than in `StanderSpec` because it is a fact about the door
   * rather than about the man: he is the one this leaf's handle is answered
   * by, and a second locked door somewhere else would name somebody else.
   * `World` looks him up once, at build, and puts a hand up on him when the
   * press comes back - see `renderGreeting`.
   *
   * Optional, and nothing breaks without it: a locked door with nobody at it
   * still refuses, still prints its label and still says its line. Dressing
   * like the rest of this struct, so naming him retires no leaderboard time.
   */
  greeter?: string;
}

/**
 * The painike on a leaf: where it is screwed, which way that face looks and
 * which way the lever reaches across it.
 *
 * Render-only, like `DecorSpec`. What it is *made of* is `DOOR_HANDLE` and
 * `World.addHandle`, because every door in the level wears the same handle -
 * this is only the half that differs from door to door, and it is three
 * numbers rather than a box because a handle is not one.
 *
 * The doors that open do not appear in this list: `World.buildDoors` works
 * theirs out from the leaf and its hinge and parents them to the leaf, so a
 * painike swings with the door it is on. These are the ones on leaves that
 * are hung on a solid wall and never move - the keittiö, the karaoke, the
 * tornimajoitus and the three WC cubicles.
 */
export interface HandleSpec {
  name: string;
  /** The middle of the rose, on the face of the leaf it is screwed to. */
  at: Vec3;
  /** Which way that face looks, as a yaw about Y. Zero is +Z. */
  rotationY: number;
  /** Which way the lever reaches: +1 to the right of somebody stood facing it. */
  reach: number;
}

/** Decor-only cylinder. Collision stays axis-aligned; these are visuals. */
export interface CylinderSpec {
  name: string;
  center: [number, number];
  radius: number;
  height: number;
  y: number;
  material: MaterialKey;
  radialSegments?: number;
  /** Set for a cone (silo roof, tent). */
  topRadius?: number;
}

/**
 * A chimney that is drawing.
 *
 * Render-only, exactly like `DecorSpec`: nothing in `src/sim` reads it and the
 * fingerprint never sees it, so a plume can be re-tuned without retiring a
 * single leaderboard time. It lives in the level rather than in `World`
 * because it is dressing with a position in the world - and because these are
 * tuning numbers, and tuning numbers are authored in `src/config/`.
 *
 * The plume is a loop, not a simulation: `count` puffs share one climb, evenly
 * spaced in age, each one reborn at the mouth when the one ahead of it is
 * spent. Nothing accumulates, so it looks the same after ten minutes as after
 * ten seconds - and a run that is paused and resumed cannot desync it.
 */
export interface SmokeSpec {
  name: string;
  /** The mouth of the pipe: where a puff is born. */
  origin: Vec3;
  /** How far a puff climbs before it is spent. */
  rise: number;
  /** Where the wind has taken it by then, in world XZ metres. */
  drift: [number, number];
  /** Puff radius at the mouth, and at the top of the climb. */
  startRadius: number;
  endRadius: number;
  /** Seconds from mouth to spent, and how many puffs are in the air. */
  life: number;
  count: number;
  /** How opaque a puff is at its thickest, 0..1. */
  density: number;
  /**
   * What the puffs are tinted, or the yard's own wood-smoke grey if this is
   * left off.
   *
   * There for exactly one plume: the savukone in front of the XLMOTO tent,
   * which is glycol out of a nozzle rather than a fire, and is therefore the
   * one thing in the level that really is white. Everything that comes out of
   * a pipe here shares the default, and the comment on `SMOKE_COLOUR` in
   * `World` says why that default is not white.
   */
  color?: number;
}

/**
 * A moving picture thrown onto a wall: the projector at the back of the
 * juhlasali, which nobody has switched off since last night.
 *
 * Geometry only, and rendering-only at that. What is *on* it is a file that
 * lives in `src/render/`, because the simulation has no business knowing that
 * the hall has a screen in it - exactly like `SmokeSpec`, this is dressing
 * with a position in the world, nothing in `src/sim` reads it, and the
 * fingerprint never sees it.
 *
 * The image is its own size rather than the screen's: a projector throws the
 * shape of what it is playing, not the shape of the thing it is aimed at.
 */
export interface ProjectionSpec {
  name: string;
  /** Centre of the image. */
  center: Vec3;
  width: number;
  height: number;
  /** Facing, read the same way as `SignSpec.rotationY`. */
  rotationY: number;
  /** The light between the lens and the sheet. */
  beam: BeamSpec;
}

/**
 * The throw itself, hanging in the air of a room nobody has aired since last
 * night - the part of a projector you actually notice from across a hall.
 *
 * It is a hollow frustum from `lens` to the four corners of the image, so the
 * beam and the picture are the same light by construction: move the screen and
 * the beam follows it, because there is only one rectangle in the level and
 * both of them are drawn from it.
 *
 * `lens` sits above the *top* of the picture rather than level with its middle,
 * and that is not a mistake. An installed projector throws its image out of the
 * bottom of the lens, not out of the centre of it, so the box hangs level from
 * the ceiling and the light still slopes down across the room. A projector
 * tilted far enough to aim at the middle of that screen would be one whose
 * picture is a trapezium.
 */
export interface BeamSpec {
  /** Apex of the light: the front of the lens glass, in world space. */
  lens: Vec3;
  /** Colour of the light in the air. */
  color: number;
  /** How much of it is visible at the lens, 0..1. */
  opacity: number;
  /** And the fraction of that left by the time it reaches the screen. */
  falloff: number;
}

/**
 * ==========================================================================
 *  THE LIGHT SHOW IN THE XLMOTO TENT
 * ==========================================================================
 *  A mirror ball, four strobe heads and two laser projectors, all plugged
 *  into the same yard tolppa as everything else - which is why they are one
 *  struct rather than three arrays. They arrived in the back of one car, they
 *  come on together and they go out together the moment somebody drags the
 *  bemari's charging cable off the post. See `World.setPowerOn`.
 *
 *  ------------------------------------------------------------------------
 *   IT IS NINE IN THE MORNING AND THIS IS THE WHOLE PROBLEM
 *  ------------------------------------------------------------------------
 *  Every one of these is a thing you look *at* in the dark, and there is no
 *  dark: the yard is lit by a low sun and a hemisphere light, and a strobe
 *  against that is a lamp somebody left on. So each of the three is drawn
 *  twice - as the fitting, which is a box, and as the light in the air, which
 *  is an additive cone or a beam that adds to whatever is already on the
 *  screen. Additive is what makes them survive daylight: a white flash over a
 *  lit yard is still brighter than the lit yard.
 *
 *  And the reason they read at all is the savukone standing with them. A beam
 *  in clear air is invisible from the side - what you actually see at a party
 *  is the smoke it is crossing. The fog machine is not a fourth toy here, it
 *  is what the other three are aimed through, and the plume in front of the
 *  tent is authored wide and low for exactly that. See `smoke`.
 *
 *  Rendering only, all of it. Nothing in `src/sim` reads a light and the
 *  fingerprint never sees one, so the whole rig can be re-timed and recoloured
 *  without retiring a leaderboard time - the same standing as `SmokeSpec` and
 *  `ProjectionSpec` above. `src/render/Disco.ts` owns *when* things flash and
 *  `World` owns what that looks like.
 * ==========================================================================
 */
export interface DiscoSpec {
  name: string;
  /** The mirror ball hung under the canopy, and how big it is. */
  ball: { center: Vec3; radius: number };
  /** The rod it hangs off, from the ball's own top to the canopy. */
  hanger: number;
  strobes: readonly StrobeSpec[];
  lasers: readonly LaserSpec[];
}

/**
 * One strobe head: a lens, a direction and a slot in the rig's cycle.
 *
 * `phase` is what stops four heads being one head. They share a period -
 * `DISCO.period` - and each one fires at a different point in it, which is
 * what turns four lamps into something moving across a tent. A rig where every
 * head is on the same beat is a rig with one head in it.
 */
export interface StrobeSpec {
  name: string;
  /** The lens, in world XYZ: the front of the glass, where the light leaves. */
  lens: Vec3;
  /** Which way it is aimed, read the same way as `SignSpec.rotationY`. */
  rotationY: number;
  /** How far it is tipped down from level, radians. Positive is downwards. */
  pitch: number;
  /** How far the cone throws, and how wide it is by then. */
  throwLength: number;
  spread: number;
  /** Where in the rig's cycle this head fires, 0..1. */
  phase: number;
  color: number;
}

/**
 * One laser projector: an apex, a fan of beams out of it, and a sweep.
 *
 * A fan rather than a single beam, because a single beam is a laser pointer
 * and a fan is a laser. The beams are drawn from one point, so the whole
 * projector turns as one object and the apex never comes apart.
 */
export interface LaserSpec {
  name: string;
  /** The projector's window, in world XYZ. */
  lens: Vec3;
  /** The middle of the fan, read the same way as `SignSpec.rotationY`. */
  rotationY: number;
  /** How far the fan is tipped up from level, radians. */
  pitch: number;
  /** How many beams, and how wide the fan opens between the outer two. */
  beams: number;
  fan: number;
  /** How long a beam is before it is lost in the yard. */
  length: number;
  /** Where in the sweep this projector starts, 0..1. */
  phase: number;
  color: number;
}

/**
 * ==========================================================================
 *  ONE TENT ON THE CAMPING FIELD
 * ==========================================================================
 *  A harjateltta: two sloping sides meeting at a ridge, a triangle at each
 *  end, and a door in one of them. That silhouette is the whole reason this
 *  is a struct of its own rather than a handful of boxes - a tent drawn out
 *  of axis-aligned slabs is a crate with a lid on it, and a field of crates
 *  is not a campsite.
 *
 *  The footprint and ridge height also define a solid box via `campTentBox`.
 *  Collision and replay compatibility use that same box; only colour is
 *  presentation. Tents on the detour must be run around, not through.
 *
 *  `color` is the one thing that is different about each one, and it is here
 *  rather than in the palette because a campsite is thirty tents in thirty
 *  colours: `World` multiplies one white flysheet tile by this, so a colour
 *  costs a material and not a texture. See `buildCampFly`.
 * ==========================================================================
 */
export interface CampTentSpec {
  name: string;
  /** Centre of the footprint, in world XZ. The floor is on the grass. */
  center: [number, number];
  /** Along the ridge, and across it. */
  length: number;
  width: number;
  /** The ridge itself, off the grass. Everything else is a fraction of these. */
  height: number;
  /**
   * Which way the door faces, read the same way as `SignSpec.rotationY` - and
   * with it the ridge, because the door is in a gable and a gable is the end
   * of a ridge. One number rather than two, so the two cannot disagree.
   */
  rotationY: number;
  /** The flysheet. Multiplied into the white tile - see above. */
  color: number;
}

/** Conservative world-aligned bounds of the rotated groundsheet and ridge. */
export function campTentBox(tent: CampTentSpec): Box {
  const sin = Math.abs(Math.sin(tent.rotationY));
  const cos = Math.abs(Math.cos(tent.rotationY));
  const halfX = (cos * tent.width + sin * tent.length) / 2;
  const halfZ = (sin * tent.width + cos * tent.length) / 2;
  return ext(
    tent.center[0] - halfX, tent.center[1] - halfZ,
    tent.center[0] + halfX, tent.center[1] + halfZ,
    tent.height,
  );
}

export interface SignSpec {
  text: string;
  position: Vec3;
  /** 0 faces +Z, PI/2 faces +X, -PI/2 faces -X, PI faces -Z. */
  rotationY: number;
  width: number;
  height: number;
  color?: string;
  background?: string;
}

/**
 * What is up on a monitor in the juhlasali.
 *
 * A kind rather than a picture: `src/render/screens.ts` draws one canvas per
 * kind and every machine showing that kind shares it. Thirty separate canvases
 * would be fifteen megabytes spent making sure two Spotify windows at opposite
 * ends of a hall are not the same playlist, which is not a thing anybody has
 * ever noticed in a room where the nearest one is two metres away.
 */
export type ScreenContent =
  | 'windows'
  | 'browser'
  | 'youtube'
  | 'spotify'
  | 'game'
  | 'video'
  | 'bsod';

/**
 * The picture on a monitor: an unlit plane hung a centimetre off its front
 * face, the same trick the valkokangas uses.
 *
 * Render-only, exactly like `DecorSpec` - nothing in `src/sim` reads it and the
 * fingerprint never sees it. Which side of the machine it faces is authored
 * rather than derived, because the thing that decides it is where the chair is,
 * and a chair is not in the level data.
 */
export interface ScreenSpec {
  name: string;
  /** Centre of the picture. */
  center: Vec3;
  width: number;
  height: number;
  /** Facing, read the same way as `SignSpec.rotationY`. */
  rotationY: number;
  content: ScreenContent;
}

/**
 * A flexible line drawn through authored points: the beer lines that run from
 * the kegs on the floor up over the end of the baaritiski to the taps.
 *
 * A hose is the one prop in the venue that cannot be a box and cannot be a
 * vertical cylinder either, which is the whole reason this type exists. The
 * points are the line's spine and `World` runs a curve through them, so three
 * or four of them are enough to describe one - see `addHose`.
 *
 * Render-only, like every other kind of dressing here.
 */
export interface HoseSpec {
  name: string;
  /** At least two, in the order the hose runs. */
  points: Vec3[];
  radius: number;
  material: MaterialKey;
}
